import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import DetailNotFound from "@/components/common/DetailNotFound";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

const NAMES = { finance: "Finance", healthcare: "Healthcare", media: "Media & Music", retail: "Retail",
    "public-sector": "Public Sector", manufacturing: "Manufacturing", education: "Education", telecom: "Telecom" };
const NOTES = { finance: "Assistants that understand policy, risk, and regulation.",
    healthcare: "Systems that respect the pace of care.",
    media: "A creative surface for stories and sound.",
    retail: "Merchandising with taste, not just throughput.",
    "public-sector": "Trust-first AI for services that matter.",
    manufacturing: "Ambient intelligence on the factory floor.",
    education: "Tutors that stay out of the way of learning.",
    telecom: "Voice-native service, at national scale." };

export default function IndustryDetail() {
    const { slug } = useParams();
    const [items, setItems] = useState([]);
    const known = Object.prototype.hasOwnProperty.call(NAMES, slug);
    const name = NAMES[slug] || slug;

    useEffect(() => {
        // Reset on every slug change — otherwise a previous industry's case
        // studies stay on screen while the new slug's request is in
        // flight. `cancelled` guards against a stale response (e.g. a slow
        // request for the previous industry) landing after a newer one
        // already resolved, or after unmount.
        let cancelled = false;
        setItems([]);
        if (known) {
            api.get(`/case-studies?industry=${slug}`).then((r) => {
                if (!cancelled) setItems(r.data?.items || []);
            });
        }
        return () => { cancelled = true; };
    }, [slug, known]);

    if (!known) {
        return (
            <DetailNotFound title="Not found — Ventara" heading="This industry is not here."
                            backTo="/industries" backLabel="Back to Industries" />
        );
    }

    return (
        <Layout title={`${name} — Ventara`} description={`Case studies from ${name}`}>
            <Section className="pt-32">
                <Link to="/industries" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Industries
                </Link>
            </Section>
            <PageHero eyebrow={`Industry · ${name}`} title={<>Case studies from<br />{name.toLowerCase()}.</>} lead={NOTES[slug] || ""} />

            <Section className="pb-32">
                <div className="border-t border-border">
                    {items.length === 0 && <p className="prose-body py-12">No case studies yet.</p>}
                    {items.map((c, i) => (
                        <Reveal key={i}>
                            <article className="grid lg:grid-cols-12 gap-8 py-12 border-b border-border group" data-testid={`case-${i}`}>
                                <div className="lg:col-span-2 label pt-2">Case 0{i + 1}</div>
                                <div className="lg:col-span-6">
                                    <p className="label">{c.client}</p>
                                    <Link to={`/industries/${slug}/${c.slug}`} className="block">
                                        <h3 className="font-serif font-light text-3xl sm:text-4xl mt-3 tracking-tight group-hover:underline underline-offset-4">{c.title}</h3>
                                    </Link>
                                    <p className="prose-body mt-5 max-w-lg">{c.note}</p>
                                    <Link to={`/industries/${slug}/${c.slug}`} className="mt-6 inline-block text-sm text-muted-foreground hover:text-foreground border-b border-transparent hover:border-foreground/40 transition-colors">
                                        Read the case study →
                                    </Link>
                                </div>
                                <div className="lg:col-span-4">
                                    <p className="label">Outcome</p>
                                    <p className="font-serif text-2xl mt-3 leading-tight">{c.outcome}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
